import React, { createContext, useContext, useState, useEffect } from 'react';
import { hardinessZones } from '../data/hardinessZones';
import { getZoneFromZipCode, isValidZipCode } from '../data/zipCodeZones';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

const defaultSettings = {
  theme: 'system', // 'light', 'dark', 'system'
  autoSave: true,
  showCompletedByDefault: true,
  enableSounds: false,
  enableNotifications: false,
  notificationFrequency: 'critical', // 'critical', 'weekly', 'daily'
  compactMode: false,
  showProgress: true,
  defaultFilter: 'all', // 'all', 'pending', 'critical', 'high'
  // Location settings
  state: 'Massachusetts',
  hardinessZone: '5b',
  zipCode: '', // New zip code field
  isFirstTimeUser: true,
  // Affiliate product recommendations
  productRecommendations: {
    enabled: true,
    showPrices: true,
    onlyEssential: false,
    maxPerTask: 3,
    userConsent: false // GDPR/CCPA compliance
  }
};

// Helper function to check for existing app data
const checkForExistingAppData = () => {
  // Check for app usage flag (most reliable indicator)
  const appUsed = localStorage.getItem('lawnCareAppUsed');
  if (appUsed) {
    return true;
  }
  
  // Don't check for welcome notification as it gets set on first load
  // This was causing race condition with the state selection modal
  
  // Check for completed tasks
  const savedTasks = localStorage.getItem('lawnCareCompletedTasks');
  if (savedTasks && savedTasks !== '[]') {
    try {
      const taskArray = JSON.parse(savedTasks);
      if (Array.isArray(taskArray) && taskArray.length > 0) {
        return true;
      }
    } catch (error) {
      // Ignore parsing errors
    }
  }
  
  // Check for task notes
  const savedNotes = localStorage.getItem('lawnCareTaskNotes');
  if (savedNotes && savedNotes !== '{}') {
    try {
      const notesObject = JSON.parse(savedNotes);
      if (notesObject && Object.keys(notesObject).length > 0) {
        return true;
      }
    } catch (error) {
      // Ignore parsing errors
    }
  }
  
  // Check for equipment data
  const savedEquipment = localStorage.getItem('lawnCareEquipment');
  if (savedEquipment && savedEquipment !== '[]') {
    try {
      const equipmentArray = JSON.parse(savedEquipment);
      if (Array.isArray(equipmentArray) && equipmentArray.length > 0) {
        return true;
      }
    } catch (error) {
      // Ignore parsing errors
    }
  }
  
  // Check for photo data
  const savedPhotos = localStorage.getItem('lawnCarePhotos');
  if (savedPhotos && savedPhotos !== '[]') {
    try {
      const photosArray = JSON.parse(savedPhotos);
      if (Array.isArray(photosArray) && photosArray.length > 0) {
        return true;
      }
    } catch (error) {
      // Ignore parsing errors
    }
  }
  
  return false;
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(defaultSettings);
  const [actualTheme, setActualTheme] = useState('light');
  const [monthlyNotes, setMonthlyNotesState] = useState({});

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('lawnCareSettings');
    let loadedSettings = { ...defaultSettings };
    
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        loadedSettings = { ...loadedSettings, ...parsed };
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    }
    
    // Always check if this is truly a first-time user by looking for any existing app data
    // This handles cases where users might have data but incorrect isFirstTimeUser flag
    const hasExistingData = checkForExistingAppData();
    
    if (hasExistingData) {
      // User has app data - they're definitely not a first-time user
      loadedSettings.isFirstTimeUser = false;
    }
    
    setSettings(loadedSettings);
  }, []);

  // Load monthly notes from localStorage on mount
  useEffect(() => {
    const savedMonthlyNotes = localStorage.getItem('lawn-smart-monthly-notes');
    if (savedMonthlyNotes) {
      try {
        const parsed = JSON.parse(savedMonthlyNotes);
        setMonthlyNotesState(parsed);
      } catch (error) {
        console.error('Error loading monthly notes:', error);
      }
    }
  }, []);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('lawnCareSettings', JSON.stringify(settings));
  }, [settings]);

  // Save monthly notes to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('lawn-smart-monthly-notes', JSON.stringify(monthlyNotes));
  }, [monthlyNotes]);

  // Handle theme changes and system preference detection with Tailwind dark mode
  useEffect(() => {
    const updateTheme = () => {
      let newTheme = settings.theme;
      
      if (settings.theme === 'system') {
        newTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      }
      
      setActualTheme(newTheme);
      
      // Update both the data-theme attribute (for CSS variables) and class (for Tailwind)
      document.documentElement.setAttribute('data-theme', newTheme);
      
      if (newTheme === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    };

    updateTheme();

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (settings.theme === 'system') {
        updateTheme();
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [settings.theme]);

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const updateLocation = ({ state, zone, zipCode }) => {
    setSettings(prev => ({ 
      ...prev, 
      state, 
      hardinessZone: zone,
      zipCode: zipCode || '',
      isFirstTimeUser: false 
    }));
  };

  const updateZipCode = (zipCode) => {
    const zone = getZoneFromZipCode(zipCode);
    if (zone) {
      setSettings(prev => ({ 
        ...prev, 
        zipCode,
        hardinessZone: zone
      }));
      return zone;
    }
    return null;
  };

  const getCurrentZoneData = () => {
    return hardinessZones[settings.hardinessZone] || hardinessZones['5b'];
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
    localStorage.removeItem('lawnCareSettings');
  };

  const setMonthlyNotes = (notes) => {
    setMonthlyNotesState(notes);
  };

  const value = {
    settings,
    actualTheme,
    updateSetting,
    updateLocation,
    updateZipCode,
    getCurrentZoneData,
    resetSettings,
    isValidZipCode,
    monthlyNotes,
    setMonthlyNotes
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};